import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import PageCard from './components/PageCard';
import useDefaultModal from './components/hooks/useDefaultModal';
import DefaultModal from './components/modals/DefaultModal';
import usePermissions from './components/hooks/usePermissions';
import LoadingModal from './components/modals/LoadingModal';
import useProgramaSituacao from './components/hooks/useProgramaSituacao';
import {
  formatCurrencyView,
  formatCurrencyInput,
  unformatCurrency,
} from './utils/currencyUtils';

const Redistribuicao = () => {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('redistribuicoes');
  
  const navigate = useNavigate();
  const { id, operacaoId, ano: anoOperacao } = useParams(); // Para edição
  const isEdit = !!id;
  const isEditOperacao = !!operacaoId;
  const anoOrcamentoOperacao = isEditOperacao
    ? parseInt(anoOperacao)
    : null;

  const [formData, setFormData] = useState({
    ano: '',
    cnpjAssociacao: '',
    // Dados do DÉBITO (origem)
    debitoIdPrograma: '',
    debitoIdAcao: '',
    debitoIdSubAcao: '',
    debitoIdElementoDespesa: '',
    debitoIdItem: '',
    // Dados do CRÉDITO (destino)
    creditoIdPrograma: '',
    creditoIdAcao: '',
    creditoIdSubAcao: '',
    creditoIdElementoDespesa: '',
    creditoIdItem: '',
    data: '',
    valor: '',
    justificativa: '',
    operacao: '',
  });

  // Estados para DÉBITO
  const [debitoProgramas, setDebitoProgramas] = useState([]);
  const [debitoAcoes, setDebitoAcoes] = useState([]);
  const [debitoSubAcoes, setDebitoSubAcoes] = useState([]);
  const [debitoItens, setDebitoItens] = useState([]);
  const [debitoSaldoInfo, setDebitoSaldoInfo] = useState(null);
  const [loadingDebitoSaldo, setLoadingDebitoSaldo] = useState(false);

  // Estados para CRÉDITO
  const [creditoProgramas, setCreditoProgramas] = useState([]);
  const [creditoAcoes, setCreditoAcoes] = useState([]);
  const [creditoSubAcoes, setCreditoSubAcoes] = useState([]);
  const [creditoItens, setCreditoItens] = useState([]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Estado para edição por operação
  const [redistribuicoesOperacao, setRedistribuicoesOperacao] = useState([]);

  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase =
    parseInt(localStorage.getItem('anoBase')) || new Date().getFullYear();

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  // Verificação de situação dos programas (vigente/encerrado)
  const { isProgramaAtivo } = useProgramaSituacao();
  // Estado computado: verificar se os programas selecionados estão ENCERRADOS
  const debitoProgramaInativo = formData.debitoIdPrograma
    ? !isProgramaAtivo(Number(formData.debitoIdPrograma))
    : false;
  const creditoProgramaInativo = formData.creditoIdPrograma
    ? !isProgramaAtivo(Number(formData.creditoIdPrograma))
    : false;
  const programaInativo = debitoProgramaInativo || creditoProgramaInativo;

  useEffect(() => {
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    loadBasicData();
    if (isEdit) {
      loadExistingData();
    } else if (isEditOperacao) {
      loadOperacaoData();
    } else {
      // Inicializar com valores padrão
      const operationNumber = generateOperationNumber();
      const dataDefault = new Date().toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        ano: anoBase.toString(),
        cnpjAssociacao: cnpj,
        data: dataDefault,
        operacao: operationNumber.toString(),
      }));
    }
  }, [podeAbrir, carregandoPermissoes]);

  // Função para gerar número de operação único
  const generateOperationNumber = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return parseInt(
      `${timestamp.toString().slice(-6)}${random.toString().padStart(3, '0')}`
    );
  };

  const loadBasicData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadDebitoProgramas(), loadCreditoProgramas()]);
    } catch (error) {
      console.error('Erro ao carregar dados básicos:', error);
      showDefaultModal(
        'error',
        'Erro ao carregar dados básicos: ' + error.message
      );
    } finally {
      setLoading(false);
    }
  };

  const loadExistingData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL.REDISTRIBUICAO}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      setFormData({
        ano: data.ano?.toString() || '',
        cnpjAssociacao: data.cnpjAssociacao || '',
        debitoIdPrograma: data.idPrograma?.toString() || '',
        debitoIdAcao: data.idAcao?.toString() || '',
        debitoIdSubAcao: data.idSubAcao?.toString() || '',
        debitoIdElementoDespesa: data.idElementoDespesa?.toString() || '',
        debitoIdItem: data.idItem?.toString() || '',
        // Para edição, crédito será o mesmo inicialmente
        creditoIdPrograma: '',
        creditoIdAcao: '',
        creditoIdSubAcao: '',
        creditoIdElementoDespesa: '',
        creditoIdItem: '',
        data: data.data || '',
        valor: formatCurrencyView(data.valor) || '',
        justificativa: data.justificativa || '',
        operacao: data.operacao?.toString() || '',
      });

      // Carregar dados relacionados ao débito
      if (data.idPrograma) {
        await loadDebitoAcoes(data.idPrograma);
        if (data.idAcao) {
          await loadDebitoSubAcoes(data.idPrograma, data.idAcao);
          await loadDebitoItens(data.idPrograma, data.idAcao, data.idSubAcao);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados para edição:', error);
      showDefaultModal(
        'error',
        'Erro ao carregar dados para edição: ' + error.message
      );
    } finally {
      setLoading(false);
    }
  };

  const loadOperacaoData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL.REDISTRIBUICAO}/operacao/${cnpj}/${
          anoOrcamentoOperacao || anoBase
        }/${operacaoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const redistribuicoes = response.data;
      setRedistribuicoesOperacao(redistribuicoes);

      if (redistribuicoes.length > 0) {
        const firstRedistribuicao = redistribuicoes[0];
        setFormData({
          ano: firstRedistribuicao.ano?.toString() || '',
          cnpjAssociacao: firstRedistribuicao.cnpjAssociacao || '',
          // Para edição de operação, não preenchemos os campos de débito/crédito específicos
          debitoIdPrograma: '',
          debitoIdAcao: '',
          debitoIdSubAcao: '',
          debitoIdElementoDespesa: '',
          debitoIdItem: '',
          creditoIdPrograma: '',
          creditoIdAcao: '',
          creditoIdSubAcao: '',
          creditoIdElementoDespesa: '',
          creditoIdItem: '',
          data: firstRedistribuicao.data || '',
          valor: '', // Não usamos valor específico na edição de operação
          justificativa: firstRedistribuicao.justificativa || '',
          operacao: firstRedistribuicao.operacao?.toString() || '',
        });
      }
    } catch (error) {
      console.error('Erro ao carregar dados da operação:', error);
      showDefaultModal(
        'error',
        'Erro ao carregar dados da operação: ' + error.message
      );
    } finally {
      setLoading(false);
    }
  };

  // Funções de carregamento para DÉBITO
  const loadDebitoProgramas = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL.PROGRAMA}/all`, {
        params: { cnpjAssociacao: cnpj },
        headers: { Authorization: `Bearer ${token}` },
      });
      setDebitoProgramas(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar programas para débito:', error);
    }
  };

  const loadDebitoAcoes = async idPrograma => {
    if (!idPrograma) {
      setDebitoAcoes([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL.ACAO}/all`, {
        params: { cnpjAssociacao: cnpj, idPrograma },
        headers: { Authorization: `Bearer ${token}` },
      });
      setDebitoAcoes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar ações para débito:', error);
      setDebitoAcoes([]);
    }
  };

  const loadDebitoSubAcoes = async (idPrograma, idAcao) => {
    if (!idPrograma || !idAcao) {
      setDebitoSubAcoes([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL.SUB_ACAO}/all`, {
        params: { cnpjAssociacao: cnpj, idPrograma, idAcao },
        headers: { Authorization: `Bearer ${token}` },
      });
      setDebitoSubAcoes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar sub-ações para débito:', error);
      setDebitoSubAcoes([]);
    }
  };

  const loadDebitoItens = async (idPrograma, idAcao, idSubAcao) => {
    if (!idPrograma || !idAcao) {
      setDebitoItens([]);
      return;
    }

    try {
      let endpoint;
      let params;

      if (idSubAcao && idSubAcao !== '') {
        endpoint = `${API_BASE_URL.ITEM}/subacao`;
        params = { cnpjAssociacao: cnpj, idPrograma, idAcao, idSubAcao };
      } else {
        endpoint = `${API_BASE_URL.ITEM}/acao`;
        params = { cnpjAssociacao: cnpj, idPrograma, idAcao };
      }

      const response = await axios.get(endpoint, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setDebitoItens(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar itens para débito:', error);
      setDebitoItens([]);
    }
  };

  // Funções de carregamento para CRÉDITO
  const loadCreditoProgramas = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL.PROGRAMA}/all`, {
        params: { cnpjAssociacao: cnpj },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCreditoProgramas(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar programas para crédito:', error);
    }
  };

  const loadCreditoAcoes = async idPrograma => {
    if (!idPrograma) {
      setCreditoAcoes([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL.ACAO}/all`, {
        params: { cnpjAssociacao: cnpj, idPrograma },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCreditoAcoes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar ações para crédito:', error);
      setCreditoAcoes([]);
    }
  };

  const loadCreditoSubAcoes = async (idPrograma, idAcao) => {
    if (!idPrograma || !idAcao) {
      setCreditoSubAcoes([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL.SUB_ACAO}/all`, {
        params: { cnpjAssociacao: cnpj, idPrograma, idAcao },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCreditoSubAcoes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar sub-ações para crédito:', error);
      setCreditoSubAcoes([]);
    }
  };

  const loadCreditoItens = async (idPrograma, idAcao, idSubAcao) => {
    if (!idPrograma || !idAcao) {
      setCreditoItens([]);
      return;
    }

    try {
      let endpoint;
      let params;

      if (idSubAcao && idSubAcao !== '') {
        endpoint = `${API_BASE_URL.ITEM}/subacao`;
        params = { cnpjAssociacao: cnpj, idPrograma, idAcao, idSubAcao };
      } else {
        endpoint = `${API_BASE_URL.ITEM}/acao`;
        params = { cnpjAssociacao: cnpj, idPrograma, idAcao };
      }

      const response = await axios.get(endpoint, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setCreditoItens(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar itens para crédito:', error);
      setCreditoItens([]);
    }
  };

  const loadDebitoSaldoDisponivel = async (
    idPrograma,
    idAcao,
    idSubAcao,
    idElementoDespesa,
    idItem
  ) => {
    if (!idPrograma || !idAcao || !idElementoDespesa || !idItem) {
      setDebitoSaldoInfo(null);
      return;
    }

    setLoadingDebitoSaldo(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL.ORCAMENTO}/saldo-disponivel-redistribuicao`,
        {
          params: {
            cnpjAssociacao: cnpj,
            ano: anoBase,
            idPrograma,
            idAcao,
            idSubAcao: idSubAcao || null,
            idElementoDespesa,
            idItem,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setDebitoSaldoInfo(response.data);
    } catch (error) {
      console.error('Erro ao carregar saldo disponível para débito:', error);
      setDebitoSaldoInfo(null);
      showDefaultModal(
        'error',
        'Erro ao consultar saldo disponível: ' +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoadingDebitoSaldo(false);
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;

    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      // Auto-seleção de elemento de despesa para DÉBITO
      if (name === 'debitoIdItem' && value && debitoItens.length > 0) {
        const itemSelecionado = debitoItens.find(
          item => item.id == value || item.id === parseInt(value)
        );
        if (itemSelecionado && itemSelecionado.idElementoDespesa) {
          newData['debitoIdElementoDespesa'] =
            itemSelecionado.idElementoDespesa.toString();

          // Carregar saldo disponível automaticamente após auto-seleção
          setTimeout(() => {
            if (formData.debitoIdPrograma && formData.debitoIdAcao) {
              loadDebitoSaldoDisponivel(
                formData.debitoIdPrograma,
                formData.debitoIdAcao,
                formData.debitoIdSubAcao,
                itemSelecionado.idElementoDespesa,
                value
              );
            }
          }, 100);
        }
      }

      // Auto-seleção de elemento de despesa para CRÉDITO
      if (name === 'creditoIdItem' && value && creditoItens.length > 0) {
        const itemSelecionado = creditoItens.find(
          item => item.id == value || item.id === parseInt(value)
        );
        if (itemSelecionado && itemSelecionado.idElementoDespesa) {
          newData['creditoIdElementoDespesa'] =
            itemSelecionado.idElementoDespesa.toString();
        }
      }

      return newData;
    });

    // Limpar erro do campo quando ele for modificado
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }

    // Lógica de dependências para DÉBITO
    if (name === 'debitoIdPrograma') {
      setFormData(prev => ({
        ...prev,
        debitoIdAcao: '',
        debitoIdSubAcao: '',
        debitoIdElementoDespesa: '',
        debitoIdItem: '',
      }));
      setDebitoAcoes([]);
      setDebitoSubAcoes([]);
      setDebitoItens([]);
      setDebitoSaldoInfo(null);
      if (value) {
        loadDebitoAcoes(value);
      }
    }

    if (name === 'debitoIdAcao') {
      setFormData(prev => ({
        ...prev,
        debitoIdSubAcao: '',
        debitoIdElementoDespesa: '',
        debitoIdItem: '',
      }));
      setDebitoSubAcoes([]);
      setDebitoItens([]);
      setDebitoSaldoInfo(null);
      if (value && formData.debitoIdPrograma) {
        loadDebitoSubAcoes(formData.debitoIdPrograma, value);
        loadDebitoItens(formData.debitoIdPrograma, value, null);
      }
    }

    if (name === 'debitoIdSubAcao') {
      setFormData(prev => ({
        ...prev,
        debitoIdElementoDespesa: '',
        debitoIdItem: '',
      }));
      setDebitoItens([]);
      setDebitoSaldoInfo(null);
      if (formData.debitoIdPrograma && formData.debitoIdAcao) {
        loadDebitoItens(
          formData.debitoIdPrograma,
          formData.debitoIdAcao,
          value
        );
      }
    }

    if (name === 'debitoIdItem') {
      setDebitoSaldoInfo(null);
      // Se o item foi limpo, limpar elemento também
      if (!value) {
        setFormData(prev => ({
          ...prev,
          debitoIdElementoDespesa: '',
        }));
      }
    }

    // Carregar saldo disponível quando elemento de despesa muda (manual ou automático)
    if (name === 'debitoIdElementoDespesa' && value && formData.debitoIdItem) {
      if (formData.debitoIdPrograma && formData.debitoIdAcao) {
        loadDebitoSaldoDisponivel(
          formData.debitoIdPrograma,
          formData.debitoIdAcao,
          formData.debitoIdSubAcao,
          value,
          formData.debitoIdItem
        );
      }
    }

    // Lógica de dependências para CRÉDITO
    if (name === 'creditoIdPrograma') {
      setFormData(prev => ({
        ...prev,
        creditoIdAcao: '',
        creditoIdSubAcao: '',
        creditoIdElementoDespesa: '',
        creditoIdItem: '',
      }));
      setCreditoAcoes([]);
      setCreditoSubAcoes([]);
      setCreditoItens([]);
      if (value) {
        loadCreditoAcoes(value);
      }
    }

    if (name === 'creditoIdAcao') {
      setFormData(prev => ({
        ...prev,
        creditoIdSubAcao: '',
        creditoIdElementoDespesa: '',
        creditoIdItem: '',
      }));
      setCreditoSubAcoes([]);
      setCreditoItens([]);
      if (value && formData.creditoIdPrograma) {
        loadCreditoSubAcoes(formData.creditoIdPrograma, value);
        loadCreditoItens(formData.creditoIdPrograma, value, null);
      }
    }

    if (name === 'creditoIdSubAcao') {
      setFormData(prev => ({
        ...prev,
        creditoIdElementoDespesa: '',
        creditoIdItem: '',
      }));
      setCreditoItens([]);
      if (formData.creditoIdPrograma && formData.creditoIdAcao) {
        loadCreditoItens(
          formData.creditoIdPrograma,
          formData.creditoIdAcao,
          value
        );
      }
    }

    if (name === 'creditoIdItem') {
      // Se o item foi limpo, limpar elemento também
      if (!value) {
        setFormData(prev => ({
          ...prev,
          creditoIdElementoDespesa: '',
        }));
      }
    }
  };

  const handleCurrencyInputChange = e => {
    const { name, value } = e.target;
    const formattedValue = formatCurrencyInput(value);
    const valorNumerico = unformatCurrency(formattedValue);

    // Validação em tempo real para valor da redistribuição
    if (name === 'valor' && debitoSaldoInfo && valorNumerico > 0) {
      if (!debitoSaldoInfo.podeRedistribuir) {
        setErrors(prev => ({
          ...prev,
          [name]:
            'Não há saldo disponível para redistribuição no item de débito',
        }));
      } else if (valorNumerico > debitoSaldoInfo.saldoDisponivel) {
        setErrors(prev => ({
          ...prev,
          [name]: `Valor excede o saldo disponível (${formatCurrencyView(
            debitoSaldoInfo.saldoDisponivel
          )})`,
        }));
      } else {
        // Valor válido, remover erro se existir
        if (errors[name]) {
          setErrors(prev => ({ ...prev, [name]: null }));
        }
      }
    } else if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }

    setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validações DÉBITO (origem)
    if (!formData.debitoIdPrograma)
      newErrors.debitoIdPrograma = 'Programa de débito é obrigatório';
    if (!formData.debitoIdAcao)
      newErrors.debitoIdAcao = 'Ação de débito é obrigatória';
    if (!formData.debitoIdElementoDespesa)
      newErrors.debitoIdElementoDespesa =
        'Elemento de despesa de débito é obrigatório';
    if (!formData.debitoIdItem)
      newErrors.debitoIdItem = 'Item de débito é obrigatório';

    // Validações CRÉDITO (destino)
    if (!formData.creditoIdPrograma)
      newErrors.creditoIdPrograma = 'Programa de crédito é obrigatório';
    if (!formData.creditoIdAcao)
      newErrors.creditoIdAcao = 'Ação de crédito é obrigatória';
    if (!formData.creditoIdElementoDespesa)
      newErrors.creditoIdElementoDespesa =
        'Elemento de despesa de crédito é obrigatório';
    if (!formData.creditoIdItem)
      newErrors.creditoIdItem = 'Item de crédito é obrigatório';

    // Validações gerais
    if (!formData.data) newErrors.data = 'Data é obrigatória';
    if (!formData.valor) newErrors.valor = 'Valor é obrigatório';
    if (!formData.justificativa || formData.justificativa.trim() === '')
      newErrors.justificativa = 'Justificativa é obrigatória';
    else if (formData.justificativa.trim().length < 10)
      newErrors.justificativa =
        'Justificativa deve ter pelo menos 10 caracteres';

    const valorNumerico = unformatCurrency(formData.valor);
    if (valorNumerico <= 0) {
      newErrors.valor = 'Valor deve ser maior que zero';
    }

    // Validar saldo disponível para redistribuição
    if (debitoSaldoInfo && valorNumerico > 0) {
      if (!debitoSaldoInfo.podeRedistribuir) {
        newErrors.valor =
          'Não há saldo disponível para redistribuição no item de débito';
      } else if (valorNumerico > debitoSaldoInfo.saldoDisponivel) {
        newErrors.valor = `Valor excede o saldo disponível (${formatCurrencyView(
          debitoSaldoInfo.saldoDisponivel
        )})`;
      }
    }

    // Validar se não está redistribuindo para o mesmo item
    if (
      formData.debitoIdPrograma &&
      formData.creditoIdPrograma &&
      formData.debitoIdAcao &&
      formData.creditoIdAcao &&
      formData.debitoIdElementoDespesa &&
      formData.creditoIdElementoDespesa &&
      formData.debitoIdItem &&
      formData.creditoIdItem
    ) {
      const mesmoItem =
        formData.debitoIdPrograma === formData.creditoIdPrograma &&
        formData.debitoIdAcao === formData.creditoIdAcao &&
        (formData.debitoIdSubAcao || '') ===
          (formData.creditoIdSubAcao || '') &&
        formData.debitoIdElementoDespesa ===
          formData.creditoIdElementoDespesa &&
        formData.debitoIdItem === formData.creditoIdItem;

      if (mesmoItem) {
        newErrors.creditoIdItem =
          'O item de crédito deve ser diferente do item de débito';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      // Criar lista de erros para exibir no modal
      const errorMessages = Object.values(newErrors);
      const errorText = errorMessages.join('\n• ');
      showDefaultModal(
        'error',
        `Corrija os seguintes erros:\n\n• ${errorText}`
      );
      return false;
    }

    setErrors({}); // Limpar erros se não houver nenhum
    return true;
  };

  const handleUpdateOperacao = async () => {
    try {
      setLoading(true);

      // Atualizar todas as redistribuições da operação com os novos dados comuns
      const updatePromises = redistribuicoesOperacao.map(redistribuicao => {
        const updatedRedistribuicao = {
          ...redistribuicao,
          data: formData.data,
          justificativa: formData.justificativa,
        };

        return axios.put(
          `${API_BASE_URL.REDISTRIBUICAO}/update`,
          updatedRedistribuicao,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      });

      await Promise.all(updatePromises);

      showDefaultModal('success', 'Operação atualizada com sucesso!');
      setTimeout(() => navigate('/redistribuicoes'), 1500);
    } catch (error) {
      console.error('Erro ao atualizar operação:', error);
      showDefaultModal(
        'error',
        'Erro ao atualizar a operação: ' + error.message
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (isEditOperacao) {
      return handleUpdateOperacao();
    }

    if (!validateForm()) {
      return;
    }

    // Bloquear se o programa de débito ou crédito estiver ENCERRADO
    const debitoProgramaInativo = formData.debitoIdPrograma
      ? !isProgramaAtivo(formData.debitoIdPrograma)
      : false;
    const creditoProgramaInativo = formData.creditoIdPrograma
      ? !isProgramaAtivo(formData.creditoIdPrograma)
      : false;

    if (debitoProgramaInativo || creditoProgramaInativo) {
      showDefaultModal(
        'error',
        'Operação bloqueada: o programa selecionado está ENCERRADO. Não é possível efetuar redistribuições para programas encerrados.'
      );
      return;
    }

    setLoading(true);
    try {
      const operacao = parseInt(formData.operacao);
      const valor = unformatCurrency(formData.valor);

      // Validar se todos os campos obrigatórios estão preenchidos
      const debitoIdPrograma = parseInt(formData.debitoIdPrograma);
      const debitoIdAcao = parseInt(formData.debitoIdAcao);
      const debitoIdElementoDespesa = parseInt(
        formData.debitoIdElementoDespesa
      );
      const debitoIdItem = parseInt(formData.debitoIdItem);

      const creditoIdPrograma = parseInt(formData.creditoIdPrograma);
      const creditoIdAcao = parseInt(formData.creditoIdAcao);
      const creditoIdElementoDespesa = parseInt(
        formData.creditoIdElementoDespesa
      );
      const creditoIdItem = parseInt(formData.creditoIdItem);

      if (
        isNaN(debitoIdPrograma) ||
        isNaN(debitoIdAcao) ||
        isNaN(debitoIdElementoDespesa) ||
        isNaN(debitoIdItem) ||
        isNaN(creditoIdPrograma) ||
        isNaN(creditoIdAcao) ||
        isNaN(creditoIdElementoDespesa) ||
        isNaN(creditoIdItem)
      ) {
        throw new Error(
          'Todos os campos de programa, ação, elemento de despesa e item são obrigatórios'
        );
      }

      // Criar registro de DÉBITO (valor negativo)
      const debitoData = {
        ano: anoBase,
        cnpjAssociacao: formData.cnpjAssociacao,
        idPrograma: debitoIdPrograma,
        idAcao: debitoIdAcao,
        idSubAcao:
          formData.debitoIdSubAcao && formData.debitoIdSubAcao.trim() !== ''
            ? parseInt(formData.debitoIdSubAcao)
            : null,
        idElementoDespesa: debitoIdElementoDespesa,
        idItem: debitoIdItem,
        data: formData.data,
        valor: -Math.abs(valor), // Valor negativo para débito
        justificativa: formData.justificativa,
        operacao: operacao,
      };

      // Criar registro de CRÉDITO (valor positivo)
      const creditoData = {
        ano: anoBase,
        cnpjAssociacao: formData.cnpjAssociacao,
        idPrograma: creditoIdPrograma,
        idAcao: creditoIdAcao,
        idSubAcao:
          formData.creditoIdSubAcao && formData.creditoIdSubAcao.trim() !== ''
            ? parseInt(formData.creditoIdSubAcao)
            : null,
        idElementoDespesa: creditoIdElementoDespesa,
        idItem: creditoIdItem,
        data: formData.data,
        valor: Math.abs(valor), // Valor positivo para crédito
        justificativa: formData.justificativa,
        operacao: operacao,
      };

      // Debug: verificar dados antes de enviar
      console.log('Dados de débito:', debitoData);
      console.log('Dados de crédito:', creditoData);

      // Salvar ambos os registros
      await Promise.all([
        axios.post(`${API_BASE_URL.REDISTRIBUICAO}/save`, debitoData, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.post(`${API_BASE_URL.REDISTRIBUICAO}/save`, creditoData, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      showDefaultModal(
        'success',
        `Redistribuição ${isEdit ? 'atualizada' : 'cadastrada'} com sucesso!`,
        () => navigate('/redistribuicoes')
      );
    } catch (error) {
      console.error('Erro ao salvar redistribuição:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data ||
        'Erro ao salvar redistribuição';
      showDefaultModal('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/redistribuicoes');
  };

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || loading) {
    return <LoadingModal loading={true} />;
  }

  return (
      <PageCard title="Redistribuicao" icon="bi-arrow-left-right" onClose={handleCancel}>
        {!podeAbrir ? (
          <div className="alert alert-warning m-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Acesso Negado</strong>
            <p className="mb-0 mt-2">
              Você não tem permissão para acessar o módulo de Redistribuição.
            </p>
            <p className="mb-0 mt-2">
              <small>Entre em contato com o administrador do sistema.</small>
            </p>
            <button 
              className="btn btn-sm btn-outline-secondary mt-3"
              onClick={() => navigate('/home')}
            >
              <i className="bi bi-arrow-left me-1"></i>
              Voltar
            </button>
          </div>
        ) : (
        <>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="text-success mb-1">
              <i className="bi bi-arrow-left-right me-2" />
              {isEditOperacao
                ? 'Editar Operação de Redistribuição'
                : isEdit
                ? 'Editar Redistribuição'
                : 'Nova Redistribuição'}
            </h4>
            <small className="text-muted">
              {isEditOperacao
                ? 'Edite os dados da operação completa (crédito e débito)'
                : isEdit
                ? 'Edite os dados da redistribuição'
                : 'Defina a origem (débito) e destino (crédito) da redistribuição orçamentária'}
            </small>
          </div>
          <div>
            <button
              type="button"
              className="btn btn-outline-secondary me-2"
              onClick={handleCancel}
              disabled={loading}
              title="Voltar para lista de redistribuições"
            >
              <i className="bi bi-arrow-left me-1" />
              Voltar
            </button>
          </div>
        </div>

        {/* Mostrar redistribuições da operação quando for edição por operação */}
        {programaInativo && (
          <div className="alert alert-warning mt-2 mb-3 mx-0" role="alert">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Um dos programas selecionados está <strong>ENCERRADO</strong>. Não é possível salvar redistribuições para programas encerrados.
          </div>
        )}
        {isEditOperacao && redistribuicoesOperacao.length > 0 && (
          <div className="card mb-4">
            <div className="card-header bg-primary text-white">
              <h6 className="mb-0">
                <i className="bi bi-list me-2" />
                Redistribuições da Operação #{operacaoId}
              </h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-sm table-striped">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Programa</th>
                      <th>Ação</th>
                      <th>SubAção</th>
                      <th>Elemento</th>
                      <th>Item</th>
                      <th>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {redistribuicoesOperacao.map((redistribuicao, index) => (
                      <tr key={index}>
                        <td>
                          <span
                            className={`badge ${
                              redistribuicao.valor < 0
                                ? 'bg-danger'
                                : 'bg-success'
                            }`}
                          >
                            {redistribuicao.valor < 0 ? 'DÉBITO' : 'CRÉDITO'}
                          </span>
                        </td>
                        <td>
                          {redistribuicao.descricaoPrograma ||
                            redistribuicao.idPrograma}
                        </td>
                        <td>
                          {redistribuicao.descricaoAcao ||
                            redistribuicao.idAcao}
                        </td>
                        <td>
                          {redistribuicao.descricaoSubAcao ||
                            redistribuicao.idSubAcao ||
                            '-'}
                        </td>
                        <td>
                          {redistribuicao.descricaoElementoDespesa ||
                            redistribuicao.idElementoDespesa}
                        </td>
                        <td>
                          {redistribuicao.descricaoItem ||
                            redistribuicao.idItem}
                        </td>
                        <td
                          className={
                            redistribuicao.valor < 0
                              ? 'text-danger'
                              : 'text-success'
                          }
                        >
                          {formatCurrencyView(Math.abs(redistribuicao.valor))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {!isEditOperacao && (
            <div className="row g-2 mb-3">
              <div className="col-md-3">
                <label className="form-label fw-bold">
                  Data da Redistribuição{' '}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className={`form-control ${
                    errors.data ? 'is-invalid' : ''
                  }`}
                  name="data"
                  value={formData.data}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.data && (
                  <div className="invalid-feedback">{errors.data}</div>
                )}
              </div>
            </div>
          )}
          <div className="row g-2">
            {/* Campos de débito e crédito - apenas para nova redistribuição ou edição individual */}
            {!isEditOperacao && (
              <>
                {/* DÉBITO - Origem dos Recursos */}
                <div className="col-md-6">
                  <div className="card border-danger">
                    <div className="card-header bg-danger text-white">
                      <h6 className="mb-0">
                        <i className="bi bi-dash-circle me-2" />
                        DÉBITO - Origem dos Recursos
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        {/* Programa Débito */}
                        <div className="col-12">
                          <label className="form-label fw-bold">
                            Programa <span className="text-danger">*</span>
                          </label>
                          <select
                            className={`form-select ${
                              errors.debitoIdPrograma ? 'is-invalid' : ''
                            }`}
                            name="debitoIdPrograma"
                            value={formData.debitoIdPrograma}
                            onChange={handleInputChange}
                            disabled={loading}
                          >
                            <option value="">Selecione um programa...</option>
                            {debitoProgramas.map(programa => (
                              <option key={programa.id} value={programa.id}>
                                {programa.descricao}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Ação Débito */}
                        <div className="col-12">
                          <label className="form-label fw-bold">
                            Ação <span className="text-danger">*</span>
                          </label>
                          <select
                            className={`form-select ${
                              errors.debitoIdAcao ? 'is-invalid' : ''
                            }`}
                            name="debitoIdAcao"
                            value={formData.debitoIdAcao}
                            onChange={handleInputChange}
                            disabled={loading || !formData.debitoIdPrograma}
                          >
                            <option value="">Selecione uma ação...</option>
                            {debitoAcoes.map(acao => (
                              <option key={acao.id} value={acao.id}>
                                {acao.descricao}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Sub-Ação Débito */}
                        <div className="col-12">
                          <label className="form-label fw-bold">Sub-Ação</label>
                          <select
                            className={`form-select ${
                              debitoSubAcoes.length === 0 &&
                              formData.debitoIdAcao
                                ? 'bg-light text-muted'
                                : ''
                            }`}
                            name="debitoIdSubAcao"
                            value={formData.debitoIdSubAcao}
                            onChange={handleInputChange}
                            disabled={
                              loading ||
                              !formData.debitoIdAcao ||
                              debitoSubAcoes.length === 0
                            }
                            title={
                              debitoSubAcoes.length === 0 &&
                              formData.debitoIdAcao
                                ? 'Esta ação não possui sub-ações'
                                : ''
                            }
                          >
                            <option value="">
                              {debitoSubAcoes.length === 0
                                ? 'Nenhuma sub-ação disponível'
                                : 'Selecione uma sub-ação...'}
                            </option>
                            {debitoSubAcoes.map(subacao => (
                              <option key={subacao.id} value={subacao.id}>
                                {subacao.descricao}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Campo oculto para Elemento Despesa Débito - preenchido automaticamente pelo item */}
                        <input
                          type="hidden"
                          name="debitoIdElementoDespesa"
                          value={formData.debitoIdElementoDespesa}
                        />

                        {/* Item Débito */}
                        <div className="col-12">
                          <label className="form-label fw-bold">
                            Item <span className="text-danger">*</span>
                          </label>
                          <select
                            className={`form-select ${
                              errors.debitoIdItem ? 'is-invalid' : ''
                            }`}
                            name="debitoIdItem"
                            value={formData.debitoIdItem}
                            onChange={handleInputChange}
                            disabled={loading || !formData.debitoIdAcao}
                          >
                            <option value="">Selecione um item...</option>
                            {debitoItens.map(item => (
                              <option key={item.id} value={item.id}>
                                {item.descricao}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CRÉDITO - Destino dos Recursos */}
                <div className="col-md-6">
                  <div className="card border-success">
                    <div className="card-header bg-success text-white">
                      <h6 className="mb-0">
                        <i className="bi bi-plus-circle me-2" />
                        CRÉDITO - Destino dos Recursos
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        {/* Programa Crédito */}
                        <div className="col-12">
                          <label className="form-label fw-bold">
                            Programa <span className="text-danger">*</span>
                          </label>
                          <select
                            className={`form-select ${
                              errors.creditoIdPrograma ? 'is-invalid' : ''
                            }`}
                            name="creditoIdPrograma"
                            value={formData.creditoIdPrograma}
                            onChange={handleInputChange}
                            disabled={loading}
                          >
                            <option value="">Selecione um programa...</option>
                            {creditoProgramas.map(programa => (
                              <option key={programa.id} value={programa.id}>
                                {programa.descricao}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Ação Crédito */}
                        <div className="col-12">
                          <label className="form-label fw-bold">
                            Ação <span className="text-danger">*</span>
                          </label>
                          <select
                            className={`form-select ${
                              errors.creditoIdAcao ? 'is-invalid' : ''
                            }`}
                            name="creditoIdAcao"
                            value={formData.creditoIdAcao}
                            onChange={handleInputChange}
                            disabled={loading || !formData.creditoIdPrograma}
                          >
                            <option value="">Selecione uma ação...</option>
                            {creditoAcoes.map(acao => (
                              <option key={acao.id} value={acao.id}>
                                {acao.descricao}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Sub-Ação Crédito */}
                        <div className="col-12">
                          <label className="form-label fw-bold">Sub-Ação</label>
                          <select
                            className={`form-select ${
                              creditoSubAcoes.length === 0 &&
                              formData.creditoIdAcao
                                ? 'bg-light text-muted'
                                : ''
                            }`}
                            name="creditoIdSubAcao"
                            value={formData.creditoIdSubAcao}
                            onChange={handleInputChange}
                            disabled={
                              loading ||
                              !formData.creditoIdAcao ||
                              creditoSubAcoes.length === 0
                            }
                            title={
                              creditoSubAcoes.length === 0 &&
                              formData.creditoIdAcao
                                ? 'Esta ação não possui sub-ações'
                                : ''
                            }
                          >
                            <option value="">
                              {creditoSubAcoes.length === 0
                                ? 'Nenhuma sub-ação disponível'
                                : 'Selecione uma sub-ação...'}
                            </option>
                            {creditoSubAcoes.map(subacao => (
                              <option key={subacao.id} value={subacao.id}>
                                {subacao.descricao}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Campo oculto para Elemento Despesa Crédito - preenchido automaticamente pelo item */}
                        <input
                          type="hidden"
                          name="creditoIdElementoDespesa"
                          value={formData.creditoIdElementoDespesa}
                        />

                        {/* Item Crédito */}
                        <div className="col-12">
                          <label className="form-label fw-bold">
                            Item <span className="text-danger">*</span>
                          </label>
                          <select
                            className={`form-select ${
                              errors.creditoIdItem ? 'is-invalid' : ''
                            }`}
                            name="creditoIdItem"
                            value={formData.creditoIdItem}
                            onChange={handleInputChange}
                            disabled={loading || !formData.creditoIdAcao}
                          >
                            <option value="">Selecione um item...</option>
                            {creditoItens.map(item => (
                              <option key={item.id} value={item.id}>
                                {item.descricao}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Informação sobre Crédito */}
                {/* <div className="col-12">
              <div className="alert alert-info mb-4">
                <div className="d-flex align-items-center">
                  <i
                    className="bi bi-info-circle me-3"
                    style={{ fontSize: '1.2rem' }}
                  ></i>
                  <div>
                    <strong>Informação sobre Crédito:</strong>
                    <span className="ms-2">
                      O valor da redistribuição será creditado no item
                      selecionado, aumentando o orçamento disponível para este
                      item específico.
                    </span>
                  </div>
                </div>
              </div>
            </div> */}

                {/* Card de Saldo Disponível com valores reais */}
                {loadingDebitoSaldo && (
                  <div className="col-12">
                    <div className="card border-info">
                      <div className="card-body text-center">
                        <div
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        >
                          <span className="visually-hidden">Carregando...</span>
                        </div>
                        Consultando saldo disponível...
                      </div>
                    </div>
                  </div>
                )}

                {debitoSaldoInfo && !loadingDebitoSaldo && (
                  <div className="col-12">
                    <div className="card border-primary">
                      <div className="card-header bg-primary text-white">
                        <h6 className="mb-0">
                          <i className="bi bi-calculator me-2"></i>
                          Saldo Orçamentário Original Disponível para
                          Redistribuição
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row small">
                          <div className="col-lg-2 col-md-4 col-6 mb-3">
                            <div className="text-center p-2 border rounded bg-light">
                              <strong className="d-block mb-1 small">
                                Orçamento Inicial:
                              </strong>
                              <span className="text-primary h6 mb-0">
                                {formatCurrencyView(
                                  debitoSaldoInfo.valorOrcamento
                                )}
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-2 col-md-4 col-6 mb-3">
                            <div className="text-center p-2 border rounded bg-light">
                              <strong className="d-block mb-1 small">
                                Débito:
                              </strong>
                              <span className="text-danger h6 mb-0">
                                {formatCurrencyView(
                                  debitoSaldoInfo.totalRedistribuicoesDebito > 0
                                    ? -debitoSaldoInfo.totalRedistribuicoesDebito
                                    : debitoSaldoInfo.totalRedistribuicoesDebito ||
                                        0
                                )}
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-2 col-md-4 col-6 mb-3">
                            <div className="text-center p-2 border rounded bg-light">
                              <strong className="d-block mb-1 small">
                                Crédito:
                              </strong>
                              <span className="text-success h6 mb-0">
                                {formatCurrencyView(
                                  debitoSaldoInfo.totalRedistribuicoesCredito >
                                    0
                                    ? debitoSaldoInfo.totalRedistribuicoesCredito
                                    : 0
                                )}
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-2 col-md-4 col-6 mb-3">
                            <div className="text-center p-2 border rounded bg-info text-white">
                              <strong className="d-block mb-1 small">
                                Orçamento Realinhado:
                              </strong>
                              <span className="h6 mb-0 fw-bold">
                                {formatCurrencyView(
                                  debitoSaldoInfo.orcamentoRedistribuido ||
                                    debitoSaldoInfo.valorOrcamento +
                                      debitoSaldoInfo.totalRedistribuicoesDebito +
                                      (debitoSaldoInfo.totalRedistribuicoesCredito ||
                                        0)
                                )}
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-2 col-md-4 col-6 mb-3">
                            <div className="text-center p-2 border rounded bg-light">
                              <strong className="d-block mb-1 small">
                                Total Pago:
                              </strong>
                              <span className="text-secondary h6 mb-0">
                                {formatCurrencyView(
                                  debitoSaldoInfo.totalDespesasPagas > 0
                                    ? -debitoSaldoInfo.totalDespesasPagas
                                    : debitoSaldoInfo.totalDespesasPagas
                                )}
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-2 col-md-8 col-12 mb-3">
                            <div
                              className={`text-center p-2 border rounded ${
                                (debitoSaldoInfo.orcamentoRedistribuido ||
                                  debitoSaldoInfo.valorOrcamento +
                                    debitoSaldoInfo.totalRedistribuicoesDebito +
                                    (debitoSaldoInfo.totalRedistribuicoesCredito ||
                                      0)) -
                                  debitoSaldoInfo.totalDespesasPagas >
                                0
                                  ? 'bg-success text-white'
                                  : 'bg-danger text-white'
                              }`}
                            >
                              <strong className="d-block mb-1 small">
                                Saldo Disponível:
                              </strong>
                              <span className="h5 mb-0 fw-bold">
                                {formatCurrencyView(
                                  (debitoSaldoInfo.orcamentoRedistribuido ||
                                    debitoSaldoInfo.valorOrcamento +
                                      debitoSaldoInfo.totalRedistribuicoesDebito +
                                      (debitoSaldoInfo.totalRedistribuicoesCredito ||
                                        0)) - debitoSaldoInfo.totalDespesasPagas
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                        {!debitoSaldoInfo.podeRedistribuir && (
                          <div className="text-center mt-2">
                            <small className="text-danger">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Não há saldo disponível para redistribuição neste
                              item.
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Seção simplificada para edição de operação */}
            {isEditOperacao ? (
              <div className="col-12">
                <div className="card border-warning">
                  <div className="card-header bg-warning text-dark">
                    <h6 className="mb-0">
                      <i className="bi bi-pencil me-2" />
                      Editar Dados da Operação
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-bold">
                          Data <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          className={`form-control ${
                            errors.data ? 'is-invalid' : ''
                          }`}
                          name="data"
                          value={formData.data}
                          onChange={handleInputChange}
                          disabled={true} // Data não editável na operação
                        />
                        {errors.data && (
                          <div className="invalid-feedback">{errors.data}</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">
                          Operação <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.operacao}
                          disabled={true} // Operação não editável
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-bold">
                          Justificativa <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className={`form-control ${
                            errors.justificativa ? 'is-invalid' : ''
                          }`}
                          name="justificativa"
                          value={formData.justificativa}
                          onChange={handleInputChange}
                          placeholder="Descreva a justificativa para esta redistribuição orçamentária..."
                          rows="4"
                          disabled={loading}
                        />
                        {errors.justificativa && (
                          <div className="invalid-feedback">
                            {errors.justificativa}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Informações Básicas - Só aparece se houver saldo disponível */
              debitoSaldoInfo &&
              debitoSaldoInfo.podeRedistribuir && (
                <div className="col-12">
                  <div className="card border-info">
                    <div className="card-header bg-info text-white">
                      <h6 className="mb-0">
                        <i className="bi bi-info-circle me-2" />
                        Informações Básicas
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-2">
                        <div className="col-md-3">
                          <label className="form-label fw-bold">
                            Valor da Redistribuição{' '}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.valor ? 'is-invalid' : ''
                            }`}
                            name="valor"
                            value={formData.valor}
                            onChange={handleCurrencyInputChange}
                            placeholder="R$ 0,00"
                            disabled={loading}
                          />
                          {errors.valor && (
                            <div className="invalid-feedback">
                              {errors.valor}
                            </div>
                          )}
                          {debitoSaldoInfo &&
                            !errors.valor &&
                            formData.valor && (
                              <div className="form-text text-success">
                                <i className="bi bi-check-circle me-1"></i>
                                Valor dentro do limite disponível:{' '}
                                {formatCurrencyView(
                                  debitoSaldoInfo.saldoDisponivel
                                )}
                              </div>
                            )}
                        </div>
                        <div className="col-md-9">
                          <label className="form-label fw-bold">
                            Justificativa <span className="text-danger">*</span>
                          </label>
                          <textarea
                            className="form-control"
                            name="justificativa"
                            value={formData.justificativa}
                            onChange={handleInputChange}
                            placeholder="Descreva a justificativa para esta redistribuição orçamentária..."
                            rows="4"
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            )}

            {/* Botões de Ação */}
            <div className="col-12">
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleCancel}
                  disabled={loading}
                  title="Cancelar e voltar"
                >
                  <i className="bi bi-x-lg me-1" />
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={
                    loading ||
                    (debitoSaldoInfo && !debitoSaldoInfo.podeRedistribuir) ||
                    (isEdit && !podeEditar) ||
                    (!isEdit && !podeCriar) ||
                    programaInativo
                  }
                  title={
                    (isEdit && !podeEditar) ? 'Você não tem permissão para editar redistribuições' :
                    (!isEdit && !podeCriar) ? 'Você não tem permissão para criar redistribuições' :
                    programaInativo ? 'Programa ENCERRADO - não é possível redistribuir' :
                    (debitoSaldoInfo && !debitoSaldoInfo.podeRedistribuir) ? 'Não há saldo disponível para redistribuição' :
                    isEditOperacao ? 'Atualizar operação de redistribuição' :
                    isEdit ? 'Atualizar redistribuição' :
                    'Salvar nova redistribuição'
                  }
                >
                  {loading ? (
                    <>
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      >
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-1" />
                      {isEditOperacao
                        ? 'Atualizar Operação'
                        : isEdit
                        ? 'Atualizar Redistribuição'
                        : 'Salvar Redistribuição'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      <DefaultModal {...setDefaultModal} />
      </>
      )}
      </PageCard>
  );
};

export default Redistribuicao;
